import type { ReactNode } from 'react';
import { useEditionStore } from '../store/edition.store';
import type { FeatureKey } from '@pokerroom/types';

/**
 * Check if a specific feature is enabled for the current edition.
 * @example const canUseICM = useFeature('tournament.icm');
 */
export function useFeature(feature: FeatureKey): boolean {
  return useEditionStore((s) => s.hasFeature(feature));
}

/**
 * Conditionally render children based on feature availability.
 * @example <FeatureGate feature="tournament.icm"><ICMButton /></FeatureGate>
 */
export function FeatureGate({
  feature,
  children,
  fallback,
}: {
  feature: FeatureKey;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const enabled = useFeature(feature);
  if (!enabled) return fallback ?? null;
  return children;
}

/**
 * Displays an upgrade hint when a feature is not available.
 */
export function UpgradeHint({ feature }: { feature: FeatureKey }) {
  const edition = useEditionStore((s) => s.edition);
  return (
    <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 text-center">
      <p className="text-sm text-warning">此功能需要升級至更高版本</p>
      <p className="mt-1 text-xs text-warning/70">
        目前版本：{edition} · 功能：{feature}
      </p>
    </div>
  );
}
